// Ações sobre um telefone que chegou no chat, seja num cartão de contato ou digitado
// solto no texto: "Copiar número" e "Iniciar conversa". Iniciar procura o número no CRM:
// achou, abre a "Nova conversa" com o contato escolhido; não achou, abre o cadastro de
// contato já preenchido e segue para a conversa quando ele é salvo.
import { useEffect, useId, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ROUTES } from '@/app/router/paths';
import { useToast } from '@/components/ui/Toast';
import { CopyIcon, MessageSquareIcon } from '@/components/icons';
import { formatPhone } from '@/lib/utils/formatters';
import { ContactCreateModal, useListContactsQuery } from '@/features/contacts';
import { nationalDigits } from '../utils/phone';
import { ConversationCreateModal } from './ConversationCreateModal';
import styles from './PhoneActions.module.css';

/** DDD + últimos 8 dígitos: casa o número com e sem o 9 (waId antigo vem sem). */
function phoneKey(phone: string): string {
  const n = nationalDigits(phone);
  return n.length >= 10 ? `${n.slice(0, 2)}${n.slice(-8)}` : n;
}

function useCopyPhone() {
  const toast = useToast();
  return async (digits: string) => {
    try {
      await navigator.clipboard.writeText(`+${digits}`);
      toast.success({ title: 'Número copiado', description: formatPhone(`+${digits}`) });
    } catch {
      toast.danger({ title: 'Não consegui copiar', description: 'Selecione o número e copie.' });
    }
  };
}

interface FlowProps {
  /** 55 + DDD + número. */
  digits: string;
  name?: string;
  onDone: () => void;
}

/** Procura no CRM e abre o modal certo. Montado só depois do clique. */
function StartConversationFlow({ digits, name, onDone }: FlowProps) {
  const navigate = useNavigate();
  const key = phoneKey(digits);
  const { data, isFetching, isError } = useListContactsQuery({ q: key.slice(-8) });
  const [createdId, setCreatedId] = useState<string | null>(null);

  if (isFetching && !data) return null;
  const match = isError ? undefined : data?.items.find((c) => phoneKey(c.phone) === key);
  const contactId = createdId ?? match?.id ?? null;

  if (contactId) {
    return (
      <ConversationCreateModal
        open
        initialContactId={contactId}
        onClose={onDone}
        onCreated={(id) => {
          onDone();
          navigate(ROUTES.inboxConversationTo(id));
        }}
      />
    );
  }
  return (
    <ContactCreateModal
      open
      initialValues={{ phone: nationalDigits(digits), name }}
      onClose={onDone}
      onCreated={(id) => setCreatedId(id)}
    />
  );
}

interface ButtonsProps {
  digits: string;
  name?: string;
}

/** As duas ações como botões (cartão de contato). */
export function PhoneActionButtons({ digits, name }: ButtonsProps) {
  const copy = useCopyPhone();
  const [starting, setStarting] = useState(false);
  const shown = formatPhone(`+${digits}`);
  return (
    <div className={styles.buttons}>
      <button
        type="button"
        className={styles.action}
        onClick={() => void copy(digits)}
        aria-label={`Copiar número ${shown}`}
      >
        <CopyIcon size={14} />
        Copiar número
      </button>
      <button
        type="button"
        className={styles.action}
        onClick={() => setStarting(true)}
        aria-label={`Iniciar conversa com ${shown}`}
      >
        <MessageSquareIcon size={14} />
        Iniciar conversa
      </button>
      {starting && (
        <StartConversationFlow digits={digits} name={name} onDone={() => setStarting(false)} />
      )}
    </div>
  );
}

interface LinkProps {
  /** Como está escrito na mensagem. */
  raw: string;
  digits: string;
}

/** Número solto no texto: vira um botão com cara de link que abre as duas ações. */
export function PhoneLink({ raw, digits }: LinkProps) {
  const copy = useCopyPhone();
  const [open, setOpen] = useState(false);
  const [starting, setStarting] = useState(false);
  const menuId = useId();
  const wrapRef = useRef<HTMLSpanElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);

  // Fecha ao clicar fora; Esc fecha e devolve o foco ao número.
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (!wrapRef.current?.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: globalThis.KeyboardEvent) => {
      if (e.key === 'Escape') {
        setOpen(false);
        triggerRef.current?.focus();
      }
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  return (
    <span className={styles.linkWrap} ref={wrapRef}>
      <button
        ref={triggerRef}
        type="button"
        className={styles.link}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls={open ? menuId : undefined}
        onClick={() => setOpen((o) => !o)}
      >
        {raw}
      </button>
      {open && (
        <span className={styles.menu} role="menu" id={menuId} aria-label={`Ações para ${raw}`}>
          <button
            type="button"
            role="menuitem"
            className={styles.menuItem}
            onClick={() => {
              setOpen(false);
              void copy(digits);
            }}
          >
            <CopyIcon size={14} />
            Copiar número
          </button>
          <button
            type="button"
            role="menuitem"
            className={styles.menuItem}
            onClick={() => {
              setOpen(false);
              setStarting(true);
            }}
          >
            <MessageSquareIcon size={14} />
            Iniciar conversa
          </button>
        </span>
      )}
      {starting && <StartConversationFlow digits={digits} onDone={() => setStarting(false)} />}
    </span>
  );
}
